import { isNonWorkspacePath } from './boot-paths';

type ChunkLoader = () => Promise<unknown>;

// Specifiers MUST resolve to the same modules as the lazy factories in
// `App.tsx` and `tab-lazy-pages.tsx`: a drifted path warms a duplicate chunk.
const loadWorkspaceLayout: ChunkLoader = async () =>
  await import('@/components/v2-production/layout');

const loadCrmPage: ChunkLoader = async () =>
  await import('@/components/v2-production/pages/crm/crm-page');

// The home tab renders the dashboard first for every office; the agentic home replaces it only
// once an office's gates answer (`HomeTabPage`), so its larger chunk is not warmed here.
const PAGE_LOADERS_BY_SEGMENT: ReadonlyMap<string, readonly ChunkLoader[]> = new Map([
  ['', [async () => await import('@/components/v2-production/pages/dashboard/dashboard-page')]],
  ['correspondence', [async () => await import('@/pages/v2/v2-correspondence-page')]],
  [
    'knowledge',
    [async () => await import('@/components/v2-production/pages/knowledge/knowledge-page')],
  ],
  ['people', [loadCrmPage]],
  ['crm', [loadCrmPage]],
  ['tasks', [async () => await import('@/pages/v2/v2-tasks-page')]],
]);

const SESSION_STORAGE_KEY = /^sb-.+-auth-token$/;

export function planWorkspaceWarm(pathname: string): readonly ChunkLoader[] {
  if (isNonWorkspacePath(pathname)) {
    return [];
  }
  const segment = pathname.replace(/^\/v2(?=\/|$)/, '').split('/')[1] ?? '';
  return [loadWorkspaceLayout, ...(PAGE_LOADERS_BY_SEGMENT.get(segment) ?? [])];
}

function hasStoredSession(storage: Storage): boolean {
  for (let index = 0; index < storage.length; index += 1) {
    if (SESSION_STORAGE_KEY.test(storage.key(index) ?? '')) {
      return true;
    }
  }
  return false;
}

/**
 * Starts the workspace layout chunk and the entry route's page chunk while the
 * entry bundle is still evaluating. `ProtectedRoute` only renders the lazy
 * layout once the organisation reads settle, so without this the whole layout
 * graph downloads after that wait instead of alongside it. A stored session
 * that turns out to be dead downloads chunks it never renders.
 */
export function warmWorkspaceAtBoot(plan: typeof planWorkspaceWarm = planWorkspaceWarm): void {
  if (import.meta.env.VITE_NATIVE_SHELL === '1') {
    return;
  }
  // A warm is an optimisation: nothing in it may stop the app from starting.
  try {
    if (!hasStoredSession(globalThis.localStorage)) {
      return;
    }
    for (const load of plan(globalThis.location.pathname)) {
      load().catch(() => {
        // A failed warm is retried by the lazy factory that needs the chunk.
      });
    }
  } catch {
    // Storage blocked, or a loader threw synchronously.
  }
}
